import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import TablePagination from '@mui/material/TablePagination';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import FilterListIcon from '@mui/icons-material/FilterList';

import useGet from 'hooks/useGet';

const statusColorMap = {
  created: 'default',
  pending: 'warning',
  authorized: 'info',
  captured: 'success',
  failed: 'error',
  cancelled: 'default',
  refunded: 'secondary'
};

const formatAmount = (currency, value) => {
  const amount = Number(value || 0);

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    return `${currency || 'INR'} ${amount.toFixed(2)}`;
  }
};

const toDisplayText = (value) => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  return String(value);
};

const PAYMENT_STATUS_FILTERS = {
  all: 'All Orders',
  created: 'Created',
  pending: 'Pending',
  authorized: 'Authorized',
  captured: 'Captured',
  failed: 'Failed',
  cancelled: 'Cancelled',
  refunded: 'Refunded'
};

const ORDER_STATUS_FILTERS = {
  all: 'All Statuses',
  pending: 'Pending',
  placed: 'Placed',
  readytoship: 'Ready To Ship',
  shipped: 'Shipped',
  delivered: 'Delivered'
};

const getPaymentStatus = (order) => String(order?.payment?.status || order?.paymentStatus || order?.status || 'unknown').toLowerCase();

const normalizeStatus = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const getOrderStatus = (order) =>
  normalizeStatus(
    order?.orderStatus || order?.dispatchStatus || order?.deliveryStatus || order?.shippingStatus || order?.trackingStatus || 'pending'
  );

const getComparableDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDateBoundary = (value, boundary) => {
  if (!value) return null;
  const date = new Date(`${value}T${boundary === 'end' ? '23:59:59.999' : '00:00:00.000'}`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return toDisplayText(value);

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(date);
};

const toTitleCase = (value) =>
  toDisplayText(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getDispatchStatus = (order) => {
  const status = order?.dispatchStatus || order?.deliveryStatus || order?.shippingStatus || order?.orderStatus || order?.trackingStatus;

  if (status) return toTitleCase(status);
  return order?.trackingNo || order?.trackingId ? 'Dispatched' : 'Dispatch Pending';
};

const getCustomerName = (order) =>
  order?.customerName || order?.userName || order?.name || order?.personalization?.name || order?.user?.name || '-';

const getOrderDate = (order) => order?.date || order?.createdAt || order?.payment?.createdAt || order?.updatedAt;

const normalizeOrdersForPage = (orders, page, limit) => {
  if (!Array.isArray(orders)) return [];
  if (!limit || orders.length <= limit) return orders;

  const expectedStart = page * limit;
  const expectedEnd = expectedStart + limit;

  if (orders.length >= expectedEnd) {
    return orders.slice(expectedStart, expectedEnd);
  }

  return orders.slice(-limit);
};

export default function OrdersTable() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const hasActiveFilters = paymentStatusFilter !== 'all' || orderStatusFilter !== 'all' || dateFrom || dateTo;
  const activeFilterCount =
    Number(paymentStatusFilter !== 'all') + Number(orderStatusFilter !== 'all') + Number(Boolean(dateFrom)) + Number(Boolean(dateTo));
  const isFilterPopoverOpen = Boolean(filterAnchorEl);

  const endpoint = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page + 1));
    params.set('limit', String(rowsPerPage));
    if (paymentStatusFilter !== 'all') {
      params.set('paymentStatus', paymentStatusFilter);
    }
    if (orderStatusFilter !== 'all') {
      params.set('orderStatus', orderStatusFilter);
    }
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    return `/api/admin/dashboard/orders?${params.toString()}`;
  }, [dateFrom, dateTo, orderStatusFilter, page, paymentStatusFilter, rowsPerPage]);

  const { data, error, loading } = useGet(endpoint);

  const incomingOrders = useMemo(() => {
    if (Array.isArray(data?.orders)) return data.orders;
    if (Array.isArray(data?.data?.orders)) return data.data.orders;
    if (Array.isArray(data)) return data;
    return [];
  }, [data]);

  const filteredIncomingOrders = useMemo(() => {
    const startDate = getDateBoundary(dateFrom, 'start');
    const endDate = getDateBoundary(dateTo, 'end');

    return incomingOrders.filter((order) => {
      if (paymentStatusFilter !== 'all' && getPaymentStatus(order) !== paymentStatusFilter) {
        return false;
      }

      if (orderStatusFilter !== 'all' && getOrderStatus(order) !== orderStatusFilter) {
        return false;
      }

      const orderDate = getComparableDate(getOrderDate(order));
      if (startDate && (!orderDate || orderDate < startDate)) {
        return false;
      }

      if (endDate && (!orderDate || orderDate > endDate)) {
        return false;
      }

      return true;
    });
  }, [dateFrom, dateTo, incomingOrders, orderStatusFilter, paymentStatusFilter]);

  const incomingPagination = useMemo(() => data?.pagination || data?.data?.pagination || {}, [data]);
  const incomingTotalOrders = Number(
    incomingPagination.totalOrders ??
      incomingPagination.total ??
      data?.totalOrders ??
      data?.total ??
      data?.count ??
      data?.totalCount ??
      data?.data?.totalOrders ??
      data?.data?.total ??
      0
  );
  const [resolvedEndpoint, setResolvedEndpoint] = useState('');
  const [resolvedOrders, setResolvedOrders] = useState([]);
  const [resolvedTotalOrders, setResolvedTotalOrders] = useState(0);

  useEffect(() => {
    if (!data) return;
    setResolvedEndpoint(endpoint);
    setResolvedOrders(normalizeOrdersForPage(filteredIncomingOrders, page, rowsPerPage));
    setResolvedTotalOrders(incomingTotalOrders);
  }, [data, endpoint, filteredIncomingOrders, incomingTotalOrders, page, rowsPerPage]);

  const hasResolvedCurrentEndpoint = resolvedEndpoint === endpoint;
  const displayOrders = hasResolvedCurrentEndpoint ? resolvedOrders : [];
  const displayTotalOrders = hasResolvedCurrentEndpoint ? resolvedTotalOrders : 0;

  const currentPage = page;
  const currentRowsPerPage = rowsPerPage;
  const hasKnownTotal = displayTotalOrders > 0 || currentPage === 0;
  const paginationCount = hasKnownTotal ? displayTotalOrders : -1;

  const hasOrders = displayOrders.length > 0;

  if (error && !hasOrders) {
    return (
      <Box sx={{ p: 2.5 }}>
        <Alert severity="error">Failed to load recent orders.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {error ? (
        <Alert severity="error" sx={{ mx: 2.5, mt: 2.5 }}>
          Failed to refresh recent orders.
        </Alert>
      ) : null}
      <Box
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          pt: { xs: 1.5, sm: 2.5 },
          pb: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant="h5">Recent Orders</Typography>
        <Tooltip title="Filter orders">
          <IconButton
            color={hasActiveFilters ? 'primary' : 'default'}
            aria-label="Filter recent orders"
            aria-haspopup="true"
            aria-expanded={isFilterPopoverOpen ? 'true' : undefined}
            onClick={(event) => setFilterAnchorEl(event.currentTarget)}
            sx={{ border: '1px solid', borderColor: hasActiveFilters ? 'primary.main' : 'divider' }}
          >
            <Badge color="primary" badgeContent={activeFilterCount} invisible={!hasActiveFilters}>
              <FilterListIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        <Popover
          open={isFilterPopoverOpen}
          anchorEl={filterAnchorEl}
          onClose={() => setFilterAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { mt: 1, p: 2, width: { xs: 300, sm: 360 }, maxWidth: 'calc(100vw - 32px)' } } }}
        >
          <Stack spacing={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel id="recent-orders-payment-filter-label">Payment Filter</InputLabel>
              <Select
                labelId="recent-orders-payment-filter-label"
                value={paymentStatusFilter}
                label="Payment Filter"
                onChange={(event) => {
                  setPaymentStatusFilter(event.target.value);
                  setPage(0);
                }}
              >
                {Object.entries(PAYMENT_STATUS_FILTERS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="recent-orders-order-filter-label">Order Filter</InputLabel>
              <Select
                labelId="recent-orders-order-filter-label"
                value={orderStatusFilter}
                label="Order Filter"
                onChange={(event) => {
                  setOrderStatusFilter(event.target.value);
                  setPage(0);
                }}
              >
                {Object.entries(ORDER_STATUS_FILTERS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                value={dateFrom}
                onChange={(event) => {
                  setDateFrom(event.target.value);
                  setPage(0);
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                value={dateTo}
                onChange={(event) => {
                  setDateTo(event.target.value);
                  setPage(0);
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => setFilterAnchorEl(null)} sx={{ textTransform: 'none' }}>
                Done
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!hasActiveFilters}
                onClick={() => {
                  setPaymentStatusFilter('all');
                  setOrderStatusFilter('all');
                  setDateFrom('');
                  setDateTo('');
                  setPage(0);
                }}
                sx={{ textTransform: 'none' }}
              >
                Clear
              </Button>
            </Stack>
          </Stack>
        </Popover>
      </Box>
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'grey.100',
          px: { xs: 1.5, sm: 2.5 },
          py: 1,
          minHeight: 180
        }}
      >
        {loading ? <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} /> : null}
        <Stack sx={{ gap: 0.75 }}>
          {displayOrders.map((order, index) => {
            const orderObjectId = order?.orderObjectId || order?._id || null;
            const displayOrderId = order?.orderId || '-';
            const paymentStatus = getPaymentStatus(order);
            const customerName = getCustomerName(order);
            const orderDate = formatDate(getOrderDate(order));
            const productName = order?.productName || order?.product?.name || '-';
            const totalAmount = order?.totalAmount ?? order?.amount ?? order?.price;

            return (
              <Box
                key={orderObjectId || order?.trackingNo || `order-card-${index}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '72px 1fr', md: '96px 1fr auto' },
                  alignItems: { xs: 'flex-start', md: 'center' },
                  gap: { xs: 2, md: 3 },
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 1, sm: 1.25 },
                  boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)'
                }}
              >
                <Avatar
                  variant="rounded"
                  src={order?.image || order?.productImage}
                  alt={productName === '-' ? 'Product' : productName}
                  sx={{
                    width: { xs: 72, md: 96 },
                    height: { xs: 72, md: 96 },
                    borderRadius: 2,
                    bgcolor: 'grey.200'
                  }}
                />
                <Stack sx={{ gap: 1, minWidth: 0 }}>
                  <Box>
                    {orderObjectId ? (
                      <Link
                        component={RouterLink}
                        to={`/payments/orders/${orderObjectId}`}
                        underline="none"
                        color="text.primary"
                        sx={{ display: 'inline-block', maxWidth: '100%', overflowWrap: 'anywhere' }}
                      >
                        <Typography variant="h5" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                          {toDisplayText(displayOrderId)}
                        </Typography>
                      </Link>
                    ) : (
                      <Typography variant="h5" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                        {toDisplayText(displayOrderId)}
                      </Typography>
                    )}
                    <Typography variant="body1" color="text.secondary">
                      User: {toDisplayText(customerName)}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                      Product: {toDisplayText(productName)}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Date: {orderDate}
                    </Typography>
                  </Box>
                  <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body1" color="text.secondary">
                      Total: {formatAmount(order?.currency, totalAmount)}
                    </Typography>
                    <Chip size="small" label={paymentStatus} color={statusColorMap[paymentStatus] || 'default'} sx={{ fontWeight: 700 }} />
                  </Stack>
                </Stack>
                <Stack
                  sx={{
                    gridColumn: { xs: '1 / -1', md: 'auto' },
                    alignItems: { xs: 'flex-start', md: 'flex-end' },
                    gap: 2,
                    justifySelf: { md: 'end' },
                    minWidth: { md: 150 }
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    {getDispatchStatus(order)}
                  </Typography>
                  {orderObjectId ? (
                    <Button
                      variant="contained"
                      component={RouterLink}
                      to={`/payments/orders/${orderObjectId}`}
                      sx={{
                        bgcolor: 'common.black',
                        color: 'common.white',
                        borderRadius: 1.5,
                        px: 2.25,
                        py: 1,
                        textTransform: 'none',
                        '&:hover': { bgcolor: 'grey.900' }
                      }}
                    >
                      View Order
                    </Button>
                  ) : null}
                </Stack>
              </Box>
            );
          })}
          {!hasOrders && !loading ? (
            <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No recent orders found.</Typography>
            </Box>
          ) : null}
          {!hasOrders && loading ? (
            <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">Loading recent orders...</Typography>
            </Box>
          ) : null}
        </Stack>
      </Box>
      <TablePagination
        component="div"
        count={paginationCount}
        page={currentPage}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={currentRowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
        sx={{
          '& .MuiTablePagination-toolbar': {
            px: { xs: 1, sm: 2 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            rowGap: 0.5
          },
          '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { m: 0 }
        }}
      />
    </Box>
  );
}
